import type { EnrollmentRow, LiveEnrollmentStatusRow, PayerReceiptEvidenceResponse } from './types';

export const PAYER_LOAD_PAGE_SIZE = 5;
export const PAYER_EVIDENCE_BATCH_SIZE = 20;

/** Keep only concrete payer ids and preserve their first-seen order. */
export function uniqueEvidencePayerIds(rows: LiveEnrollmentStatusRow[]): string[] {
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const row of rows) {
    const payerId = row.stedi_payer_id?.trim();
    if (!payerId) continue;
    const key = payerId.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    ids.push(payerId);
  }
  return ids;
}

/** The backend accepts at most 20 payer ids in one evidence request. */
export function evidencePayerIdBatches(payerIds: string[]): string[][] {
  const batches: string[][] = [];
  for (let index = 0; index < payerIds.length; index += PAYER_EVIDENCE_BATCH_SIZE) {
    batches.push(payerIds.slice(index, index + PAYER_EVIDENCE_BATCH_SIZE));
  }
  return batches;
}

/**
 * Add receipt evidence to status-only rows for the existing fold. Missing ids
 * and unresolved evidence stay unknown until a concrete response proves true
 * or false.
 */
export function mergeStatusAndEvidence(
  rows: LiveEnrollmentStatusRow[],
  evidence: Iterable<PayerReceiptEvidenceResponse>,
): EnrollmentRow[] {
  const canonicalPayerIds = new Map<string, string>();
  for (const row of rows) {
    const payerId = row.stedi_payer_id?.trim();
    if (!payerId) continue;
    const key = payerId.toLowerCase();
    if (!canonicalPayerIds.has(key)) canonicalPayerIds.set(key, payerId);
  }

  const byPayerId = new Map<string, PayerReceiptEvidenceResponse>();
  for (const item of evidence) {
    const key = item.payer_id.trim().toLowerCase();
    const previous = byPayerId.get(key);
    if (previous?.has_received_claim === true && item.has_received_claim !== true) continue;
    byPayerId.set(key, item);
  }

  return rows.map(row => {
    const payerIdKey = row.stedi_payer_id?.trim().toLowerCase();
    const item = payerIdKey ? byPayerId.get(payerIdKey) : undefined;
    return {
      ...row,
      stedi_payer_id: payerIdKey ? canonicalPayerIds.get(payerIdKey) : row.stedi_payer_id,
      has_received_claim: item?.has_received_claim ?? null,
      evidence_source: item?.evidence_source ?? null,
      evidence_error: item?.evidence_error ?? null,
    };
  });
}

export type PayerLoadFailureClass = 'timeout' | 'transport' | 'http';

export interface PayerLoadFailure {
  failureClass: PayerLoadFailureClass;
  message: string;
}

/** Read auth0BaseQuery's bounded metadata, with safe fallbacks for legacy errors. */
export function payerLoadFailure(error: unknown): PayerLoadFailure {
  if (typeof error === 'object' && error) {
    const outer = error as {
      status?: unknown;
      data?: unknown;
      failureClass?: unknown;
      message?: unknown;
    };
    const inner =
      typeof outer.data === 'object' && outer.data
        ? (outer.data as { failureClass?: unknown; message?: unknown })
        : undefined;
    const classified = inner?.failureClass ?? outer.failureClass;
    const failureClass: PayerLoadFailureClass =
      classified === 'timeout' || classified === 'transport' || classified === 'http'
        ? classified
        : outer.status === 'TIMEOUT_ERROR'
          ? 'timeout'
          : typeof outer.status === 'number'
            ? 'http'
            : 'transport';
    const message =
      typeof inner?.message === 'string'
        ? inner.message
        : typeof outer.message === 'string'
          ? outer.message
          : failureClass === 'timeout'
            ? 'Request timed out. Please try again.'
            : failureClass === 'transport'
              ? 'The service could not be reached. Please try again.'
              : 'The request failed. Please try again.';
    return { failureClass, message };
  }

  return {
    failureClass: 'transport',
    message: 'The service could not be reached. Please try again.',
  };
}
